export interface ImportRowOutcome {
  id?: string;
  row: number;
  email: string;
  status: string;
  reasonCode?: string | null;
  reason?: string | null;
  reconciliationAction?: string | null;
  reconciliationResult?: string | null;
  correctionOfRowId?: string | null;
}

export interface ImportBatchStatusResponse {
  id: string;
  companyId: string;
  status: string;
  currentPhase: 'validating' | 'running' | 'compensating' | 'completed';
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  reconciliationRequiredCount: number;
  failedCount: number;
  pendingCount: number;
  compensatedRows: number;
  compensationFailedRows: number;
  lastError: string | null;
  recoveryAvailable?: boolean;
  recoveryReason?: string | null;
  executionLeaseExpiresAt?: string | null;
  rows: ImportRowOutcome[];
}

export const terminalImportStatuses = [
  'succeeded', 'completed_with_skips', 'completed_with_issues', 'completed_with_reconciliation_required',
  'failed_no_side_effects', 'failed_compensated', 'failed_manual_reconciliation_required',
];

export function maskedImportReport(batchId: string, rows: ImportRowOutcome[]): string {
  const cell = (value: string | number) => '"' + String(value).replace(/^[=+\-@\t\r]/, "'$&").replace(/"/g, '""') + '"';
  return [
    ['Batch', 'Row', 'Import row ID', 'Email (masked)', 'Status', 'Reason code', 'Previous Import Row ID'],
    ...rows.map((row) => [batchId, row.row, row.id || '', row.email ? '***@***' : '', row.status,
      row.reasonCode || '', row.correctionOfRowId || row.id || '']),
  ].map((values) => values.map(cell).join(',')).join('\r\n');
}

export interface CustomerImportBatchSummary {
  id: string;
  status: string;
  sourceFilename: string | null;
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  reconciliationRequiredCount: number;
  failedCount: number;
  createdAt: string;
  completedAt: string | null;
}

export interface CustomerImportHistory {
  items: CustomerImportBatchSummary[];
  total: number;
  offset: number;
  limit: number;
}

export interface CustomerImportRecoveryTarget {
  batchId: string;
  rowId: string;
  status: string;
  batchStatus: string;
  recoveryAvailable: boolean;
  recoveryReason: string | null;
}

export type CustomerImportReviewDecision = 'use_existing' | 'retry_unstarted' | 'exclude';
export interface CustomerImportReview {
  rowId: string;
  batchId: string;
  rowNumber: number;
  status: string;
  expectedFingerprint: string;
  classification: string;
  candidate: { id: string; name: string } | null;
  originals: { rowId: string; batchId: string; status: string; batchStatus: string }[];
  actions: { decision: CustomerImportReviewDecision; enabled: boolean; reason: string }[];
  evidence: { provider: string; observedAt: string; complete: boolean };
  reviewed?: { decision: CustomerImportReviewDecision; reason: string; createdAt: string };
  notes: string[];
}

export interface CustomerImportReviewRequest {
  decision: CustomerImportReviewDecision;
  expectedFingerprint: string;
  reason: string;
  confirm: true;
}

export function customerImportScope(host?: string, companyId?: string, actorId?: string): string | null {
  return host && companyId && actorId ? JSON.stringify([host, companyId, actorId]) : null;
}

export function customerImportStorageKey(scope: string): string {
  return `admin.customerImport.activeBatchId:v2:${scope}`;
}

export function unresolvedImportRow(row: ImportRowOutcome): boolean {
  return ['pending', 'validating', 'ready', 'creating_local', 'creating_stripe', 'failed',
    'identity_conflict', 'blocked_stripe_only_duplicate', 'manual_reconciliation_required', 'skipped_reconciliation_required'].includes(row.status);
}
